import React, { PureComponent } from "react";
import _ from "lodash";
import autoBind from "react-autobind";
import { Alert, Button, ButtonGroup, FormControl, Label, Tab, Tabs } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Controlled as CodeMirror } from "react-codemirror2";

import { getDriveObject, getUrlForExternalResource } from "../../../actions/drive";
import { codeMirrorOptionsJson } from "../../../constants/appConstants";
import { FLOORPLAN_DRAFT_SCHEMA_VERSION, validateFloorplanDraft } from "../../../utils/floorplanImportUtils";

import "./floorplanStudioEditor.css";

require("codemirror/lib/codemirror.css"); // eslint-disable-line
require("codemirror/mode/javascript/javascript"); // eslint-disable-line
require("codemirror/addon/fold/foldcode"); // eslint-disable-line
require("codemirror/addon/fold/foldgutter"); // eslint-disable-line
require("codemirror/addon/fold/brace-fold"); // eslint-disable-line
require("codemirror/addon/fold/foldgutter.css"); // eslint-disable-line
require("codemirror/addon/lint/lint"); // eslint-disable-line
require("codemirror/addon/lint/json-lint"); // eslint-disable-line
require("codemirror/addon/lint/lint.css"); // eslint-disable-line

const MIN_ZOOM = 0.02;
const MAX_ZOOM = 8;
const FIT_PADDING = 32;
const FLOORPLAN_JSON_EDITOR_OPTIONS = {
	...codeMirrorOptionsJson,
	viewportMargin: 20
};

const ASSET_ID_FIELDS = [
	"maskBehindAssetId",
	"imageAssetId",
	"origImageAssetId",
	"originalImageAssetId",
	"maskOverAssetId"
];

function asArray(value) {
	return _.isArray(value) ? value : [];
}

function finiteNumber(value, fallback = 0) {
	const number = Number(value);
	return Number.isFinite(number) ? number : fallback;
}

function clamp(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function createEmptyDraft() {
	return {
		schemaVersion: FLOORPLAN_DRAFT_SCHEMA_VERSION,
		id: "",
		name: "Untitled Floorplan",
		title: "",
		source: null,
		editorState: {
			currentFloorIndex: 0,
			currentEntry: "a"
		},
		rules: [],
		floors: [],
		assets: {}
	};
}

function serializeDraft(draft) {
	return JSON.stringify(draft || createEmptyDraft(), null, 4);
}

function parseContent(content) {
	if (_.isPlainObject(content)) {
		return _.cloneDeep(content);
	}
	if (!_.isString(content) || content.trim() === "") {
		return createEmptyDraft();
	}
	return JSON.parse(content);
}

function validateDraft(draft) {
	const validation = validateFloorplanDraft(draft);
	return asArray(validation && validation.errors).map((issue) => {
		if (_.isString(issue)) {
			return issue;
		}
		const path = issue && issue.path ? `${issue.path}: ` : "";
		return `${path}${issue && issue.message ? issue.message : "Invalid floorplan draft."}`;
	});
}

function getDraftTitle(draft) {
	return _.toString((draft && (draft.title || draft.name || draft.projectTitle || draft.projectName)) || "Untitled Floorplan");
}

function getFloors(draft) {
	return asArray(draft && draft.floors);
}

function getAssets(draft) {
	if (_.isPlainObject(draft && draft.assets)) {
		return draft.assets;
	}
	const result = {};
	asArray(draft && draft.assets).forEach((asset) => {
		if (asset && asset.id !== undefined && asset.id !== null) {
			result[asset.id] = asset;
		}
	});
	return result;
}

function getFloorIndex(draft) {
	const floors = getFloors(draft);
	const requested = finiteNumber(
		_.get(draft, "editorState.currentFloorIndex", draft && draft.currentFloorIndex),
		0
	);
	return floors.length ? clamp(Math.floor(requested), 0, floors.length - 1) : 0;
}

function getActiveSlot(draft, floor) {
	if (!floor) {
		return "a";
	}
	if (floor.variantMode === "entry") {
		return _.get(draft, "editorState.currentEntry", draft.currentEntry) === "b" ? "b" : "a";
	}
	if (floor.variantMode === "local" || floor.baseB) {
		return floor.localActiveSlot === "b" ? "b" : "a";
	}
	return "a";
}

function getSlotLabels(floor) {
	if (floor && floor.variantMode === "entry") {
		return ["Front Entry", "Side Entry"];
	}
	if (floor && floor.variantMode === "local") {
		return [floor.localLabelA || "Option A", floor.localLabelB || "Option B"];
	}
	return ["Base A", "Base B"];
}

function getActiveBase(floor, activeSlot) {
	if (!floor) {
		return null;
	}
	return activeSlot === "b" ? (floor.baseB || null) : (floor.base || null);
}

function getFrame(record, fallbackWidth = 0, fallbackHeight = 0) {
	const frame = (record && record.frame) || {};
	const naturalSize = (record && record.naturalSize) || {};
	return {
		x: finiteNumber(record && record.x, finiteNumber(frame.x, 0)),
		y: finiteNumber(record && record.y, finiteNumber(frame.y, 0)),
		width: finiteNumber(record && record.w, finiteNumber(frame.width, finiteNumber(record && record.naturalW, finiteNumber(naturalSize.width, fallbackWidth)))),
		height: finiteNumber(record && record.h, finiteNumber(frame.height, finiteNumber(record && record.naturalH, finiteNumber(naturalSize.height, fallbackHeight))))
	};
}

function getCanvasSize(floor, base) {
	const canvas = (floor && floor.canvas) || {};
	const baseFrame = getFrame(base);
	const width = finiteNumber(canvas.width, finiteNumber(base && base.naturalW, baseFrame.width));
	const height = finiteNumber(canvas.height, finiteNumber(base && base.naturalH, baseFrame.height));

	return {
		width: Math.max(1, Math.ceil(width || 1000)),
		height: Math.max(1, Math.ceil(height || 1000))
	};
}

function getAssetDescriptor(assets, assetId) {
	if (!assetId && assetId !== 0) {
		return null;
	}
	return assets[assetId] || assets[`${assetId}`] || null;
}

function getStudioRendition(draft, assetId) {
	if (!assetId && assetId !== 0) {
		return null;
	}
	return _.get(draft, ["renditions", "profiles", "studio", "assets", `${assetId}`], null);
}

function getRenderableAsset(draft, assets, assetId) {
	return getStudioRendition(draft, assetId) || getAssetDescriptor(assets, assetId);
}

function getAssetUrl(asset) {
	if (!asset) {
		return "";
	}

	const directUrl = asset.url || asset.signedUrl || asset.signed_url;
	if (_.isString(directUrl) && /^(https?:|data:|blob:)/i.test(directUrl)) {
		return directUrl;
	}

	const resource = asset.resource || asset.driveResource || asset.reference || asset;
	if (_.isString(resource) && /^(https?:|data:|blob:)/i.test(resource)) {
		return resource;
	}

	try {
		if (resource && resource.guid) {
			const driveObject = getDriveObject(resource.guid);
			if (driveObject) {
				return getUrlForExternalResource({
					...driveObject,
					revision: resource.revision || driveObject.revision
				});
			}
		}
		if (resource && resource.path) {
			return getUrlForExternalResource(resource);
		}
	} catch (_err) {
		return "";
	}
	return "";
}

function getPreferredArtworkAssetId(record) {
	if (!record) {
		return null;
	}
	const artworkComponent = asArray(record.components).find((component) => component && component.role === "artwork");
	return record.imageAssetId || record.assetId || (artworkComponent && artworkComponent.assetId) || record.origImageAssetId || record.originalImageAssetId || null;
}

function getReferencedAssetIds(record) {
	const result = [];
	if (!record) {
		return result;
	}
	ASSET_ID_FIELDS.forEach((field) => {
		if (record[field]) {
			result.push(record[field]);
		}
	});
	if (record.assetId) {
		result.push(record.assetId);
	}
	asArray(record.components).forEach((component) => {
		if (component && component.assetId) {
			result.push(component.assetId);
		}
	});
	return _.uniq(result);
}

function getRecordComponents(record) {
	if (!record) {
		return [];
	}
	if (_.isArray(record.components)) {
		return record.components;
	}
	return _.compact([
		record.maskBehindAssetId ? { role: "maskBehind", assetId: record.maskBehindAssetId } : null,
		getPreferredArtworkAssetId(record) ? { role: "artwork", assetId: getPreferredArtworkAssetId(record) } : null,
		record.maskOverAssetId ? { role: "maskOver", assetId: record.maskOverAssetId } : null
	]);
}

function isLayerInScope(layer, activeSlot) {
	return !layer || !layer.scope || layer.scope === "both" || layer.scope === activeSlot;
}

export default class FloorplanStudioEditor extends PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);

		const parsed = this.parseWithStatus(props.content);
		const draft = parsed.draft;
		const jsonText = _.isString(props.content) && props.content.trim() !== ""
			? props.content
			: serializeDraft(draft);
		this.lastEmittedContent = jsonText;
		this.viewport = React.createRef();
		this.panSession = null;
		this.fitRequest = null;
		this.state = {
			activeTab: parsed.error || parsed.schemaErrors.length ? "json" : "visual",
			draft,
			jsonText,
			jsonError: parsed.error,
			schemaErrors: parsed.schemaErrors,
			floorIndex: draft ? getFloorIndex(draft) : 0,
			zoom: 1,
			panX: FIT_PADDING,
			panY: FIT_PADDING,
			panning: false,
			imageErrors: {}
		};

	}

	componentDidMount() {
		window.addEventListener("mousemove", this.handleWindowMouseMove);
		window.addEventListener("mouseup", this.handleWindowMouseUp);
		window.addEventListener("resize", this.handleWindowResize);
		this.emitChange(
			this.state.jsonText,
			!this.state.jsonError && this.state.schemaErrors.length === 0,
			this.state.jsonError || this.state.schemaErrors.join(" ")
		);
		this.requestFit();
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.content !== this.props.content) {
			const incoming = _.isString(this.props.content) ? this.props.content : serializeDraft(this.props.content);
			if (incoming !== this.lastEmittedContent) {
				const parsed = this.parseWithStatus(this.props.content);
				const draft = parsed.draft;
				const jsonText = _.isString(this.props.content) ? this.props.content : serializeDraft(draft);
				this.lastEmittedContent = jsonText;
				this.setState({
					draft,
					jsonText,
					jsonError: parsed.error,
					schemaErrors: parsed.schemaErrors,
					floorIndex: draft ? getFloorIndex(draft) : 0,
					imageErrors: {}
				}, this.requestFit);
				this.emitChange(jsonText, !parsed.error && parsed.schemaErrors.length === 0, parsed.error || parsed.schemaErrors.join(" "));
			}
		}

		if (prevState.floorIndex !== this.state.floorIndex) {
			this.requestFit();
		}
	}

	componentWillUnmount() {
		window.removeEventListener("mousemove", this.handleWindowMouseMove);
		window.removeEventListener("mouseup", this.handleWindowMouseUp);
		window.removeEventListener("resize", this.handleWindowResize);
		if (this.fitRequest && typeof cancelAnimationFrame === "function") {
			cancelAnimationFrame(this.fitRequest);
		}
	}

	getContent() {
		if (this.state.jsonError || this.state.schemaErrors.length) {
			return this.state.jsonText;
		}
		return serializeDraft(this.state.draft);
	}

	parseWithStatus(content) {
		try {
			const draft = parseContent(content);
			return { draft, error: "", schemaErrors: validateDraft(draft) };
		} catch (err) {
			return { draft: null, error: err.message || "Unable to parse JSON.", schemaErrors: [] };
		}
	}

	emitChange(content, valid, errorMessage = "") {
		this.lastEmittedContent = content;
		if (_.isFunction(this.props.onChange)) {
			this.props.onChange(content);
		}
		if (_.isFunction(this.props.onValidityChange)) {
			this.props.onValidityChange({
				valid,
				error: valid ? null : new Error(errorMessage || "Invalid floorplan draft.")
			});
		}
	}

	applyDraft(nextDraft, callback) {
		const schemaErrors = validateDraft(nextDraft);
		const jsonText = serializeDraft(nextDraft);
		this.setState({
			draft: nextDraft,
			jsonText,
			jsonError: "",
			schemaErrors
		}, callback);
		this.emitChange(jsonText, schemaErrors.length === 0, schemaErrors.join(" "));
	}

	handleJsonChange(valueOrEvent) {
		const jsonText = _.isString(valueOrEvent) ? valueOrEvent : _.get(valueOrEvent, "target.value", "");
		try {
			const draft = JSON.parse(jsonText);
			const schemaErrors = validateDraft(draft);
			this.setState({
				draft,
				jsonText,
				jsonError: "",
				schemaErrors,
				floorIndex: getFloorIndex(draft),
				imageErrors: {}
			}, schemaErrors.length ? null : this.requestFit);
			this.emitChange(jsonText, schemaErrors.length === 0, schemaErrors.join(" "));
		} catch (err) {
			this.setState({
				jsonText,
				jsonError: err.message || "Unable to parse JSON.",
				schemaErrors: []
			});
			this.emitChange(jsonText, false, err.message);
		}
	}

	handleFloorChange(event) {
		const floors = getFloors(this.state.draft);
		const floorIndex = clamp(finiteNumber(event.target.value, 0), 0, Math.max(floors.length - 1, 0));
		const draft = _.cloneDeep(this.state.draft);
		draft.editorState = {
			...(draft.editorState || {}),
			currentFloorIndex: floorIndex
		};
		if (Object.prototype.hasOwnProperty.call(draft, "currentFloorIndex")) {
			draft.currentFloorIndex = floorIndex;
		}
		this.setState({ floorIndex, imageErrors: {} });
		this.applyDraft(draft, this.requestFit);
	}

	handleSlotChange(event) {
		const slot = event.target.value === "b" ? "b" : "a";
		const draft = _.cloneDeep(this.state.draft);
		const floor = getFloors(draft)[this.state.floorIndex];
		if (!floor) {
			return;
		}

		if (floor.variantMode === "entry") {
			draft.editorState = {
				...(draft.editorState || {}),
				currentEntry: slot
			};
			if (Object.prototype.hasOwnProperty.call(draft, "currentEntry")) {
				draft.currentEntry = slot;
			}
		} else {
			floor.localActiveSlot = slot;
		}
		this.setState({ imageErrors: {} });
		this.applyDraft(draft, this.requestFit);
	}

	handleLayerVisibilityChange(layerIndex, event) {
		const draft = _.cloneDeep(this.state.draft);
		const floor = getFloors(draft)[this.state.floorIndex];
		if (!floor || !floor.layers || !floor.layers[layerIndex]) {
			return;
		}
		floor.layers[layerIndex].visible = event.target.checked;
		this.applyDraft(draft);
	}

	requestFit() {
		if (this.fitRequest && typeof cancelAnimationFrame === "function") {
			cancelAnimationFrame(this.fitRequest);
		}
		if (typeof requestAnimationFrame === "function") {
			this.fitRequest = requestAnimationFrame(() => {
				this.fitRequest = null;
				this.fitToView();
			});
		} else {
			this.fitToView();
		}
	}

	handleWindowResize() {
		this.requestFit();
	}

	fitToView() {
		const viewport = this.viewport.current;
		const { floor, base } = this.getCurrentContext();
		if (!viewport || !floor) {
			return;
		}
		const canvas = getCanvasSize(floor, base);
		const availableWidth = Math.max(1, viewport.clientWidth - FIT_PADDING * 2);
		const availableHeight = Math.max(1, viewport.clientHeight - FIT_PADDING * 2);
		const zoom = clamp(Math.min(availableWidth / canvas.width, availableHeight / canvas.height), MIN_ZOOM, MAX_ZOOM);
		this.setState({
			zoom,
			panX: (viewport.clientWidth - canvas.width * zoom) / 2,
			panY: (viewport.clientHeight - canvas.height * zoom) / 2
		});
	}

	setZoom(nextZoom, anchorX = null, anchorY = null) {
		const viewport = this.viewport.current;
		const zoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
		if (!viewport || zoom === this.state.zoom) {
			return;
		}
		const x = anchorX === null ? viewport.clientWidth / 2 : anchorX;
		const y = anchorY === null ? viewport.clientHeight / 2 : anchorY;
		const canvasX = (x - this.state.panX) / this.state.zoom;
		const canvasY = (y - this.state.panY) / this.state.zoom;
		this.setState({
			zoom,
			panX: x - canvasX * zoom,
			panY: y - canvasY * zoom
		});
	}

	handleZoomIn() {
		this.setZoom(this.state.zoom * 1.25);
	}

	handleZoomOut() {
		this.setZoom(this.state.zoom / 1.25);
	}

	handleZoomActual() {
		const viewport = this.viewport.current;
		const { floor, base } = this.getCurrentContext();
		if (!viewport || !floor) {
			return;
		}
		const canvas = getCanvasSize(floor, base);
		this.setState({
			zoom: 1,
			panX: (viewport.clientWidth - canvas.width) / 2,
			panY: (viewport.clientHeight - canvas.height) / 2
		});
	}

	handleWheel(event) {
		event.preventDefault();
		const viewport = this.viewport.current;
		if (!viewport) {
			return;
		}
		const rect = viewport.getBoundingClientRect();
		this.setZoom(
			this.state.zoom * (event.deltaY < 0 ? 1.12 : 1 / 1.12),
			event.clientX - rect.left,
			event.clientY - rect.top
		);
	}

	handlePanMouseDown(event) {
		if (event.button !== 0 && event.button !== 1) {
			return;
		}
		event.preventDefault();
		this.panSession = {
			clientX: event.clientX,
			clientY: event.clientY,
			panX: this.state.panX,
			panY: this.state.panY
		};
		this.setState({ panning: true });
	}

	handleWindowMouseMove(event) {
		if (!this.panSession) {
			return;
		}
		this.setState({
			panX: this.panSession.panX + event.clientX - this.panSession.clientX,
			panY: this.panSession.panY + event.clientY - this.panSession.clientY
		});
	}

	handleWindowMouseUp() {
		if (!this.panSession) {
			return;
		}
		this.panSession = null;
		this.setState({ panning: false });
	}

	handleImageError(assetId) {
		if (this.state.imageErrors[assetId]) {
			return;
		}
		this.setState({
			imageErrors: {
				...this.state.imageErrors,
				[assetId]: true
			}
		});
	}

	getCurrentContext() {
		const draft = this.state.draft;
		const floors = getFloors(draft);
		const floor = floors[this.state.floorIndex] || null;
		const activeSlot = getActiveSlot(draft, floor);
		const base = getActiveBase(floor, activeSlot);
		return { draft, floors, floor, activeSlot, base, assets: getAssets(draft) };
	}

	renderImageComponent(component, record, frame, zIndex, opacity, blendMode, keyPrefix) {
		const assets = getAssets(this.state.draft);
		const asset = getRenderableAsset(this.state.draft, assets, component && component.assetId);
		const url = getAssetUrl(asset);
		if (!url) {
			return null;
		}
		return (
			<img
				key={`${keyPrefix}-${component.role}-${component.assetId}`}
				className="floorplan-studio__canvas-image"
				src={url}
				alt=""
				draggable={false}
				onError={() => this.handleImageError(component.assetId)}
				style={{
					left: `${frame.x}px`,
					top: `${frame.y}px`,
					width: `${frame.width}px`,
					height: `${frame.height}px`,
					zIndex,
					opacity: component.role === "artwork" ? opacity : 1,
					mixBlendMode: component.role === "artwork" ? blendMode : "normal"
				}}
			/>
		);
	}

	renderRecordComponents(record, frame, baseZIndex, keyPrefix) {
		const opacity = finiteNumber(record && record.opacity, 1);
		const requestedBlendMode = (record && (record.blend || record.blendMode)) || "normal";
		const blendMode = requestedBlendMode === "source-over" ? "normal" : requestedBlendMode;
		return getRecordComponents(record).map((component) => {
			let zIndex = baseZIndex;
			if (component.role === "maskBehind") {
				zIndex = baseZIndex - 1;
			} else if (component.role === "maskOver") {
				zIndex = baseZIndex + 1;
			}
			return this.renderImageComponent(component, record, frame, zIndex, opacity, blendMode, keyPrefix);
		});
	}

	renderCanvas() {
		const { floor, activeSlot, base } = this.getCurrentContext();
		if (!floor) {
			return (
				<div className="floorplan-studio__canvas-empty">
					<strong>No floors yet</strong><br />
					Import a project or add a floor in a later authoring milestone.
				</div>
			);
		}

		const canvas = getCanvasSize(floor, base);
		const baseOffsetX = activeSlot === "b" ? floor.baseBOffsetX : floor.baseAOffsetX;
		const baseOffsetY = activeSlot === "b" ? floor.baseBOffsetY : floor.baseAOffsetY;
		const baseFrame = getFrame(base, canvas.width, canvas.height);
		baseFrame.x += finiteNumber(baseOffsetX, 0);
		baseFrame.y += finiteNumber(baseOffsetY, 0);
		const layers = asArray(floor.layers);
		const visibleLayers = layers.filter((layer) => layer && layer.visible !== false && isLayerInScope(layer, activeSlot));
		const hasBaseArtwork = !!getPreferredArtworkAssetId(base);

		return (
			<div
				className="floorplan-studio__canvas"
				style={{
					width: `${canvas.width}px`,
					height: `${canvas.height}px`,
					transform: `translate(${this.state.panX}px, ${this.state.panY}px) scale(${this.state.zoom})`
				}}
			>
				{base && this.renderRecordComponents(base, baseFrame, 1, `base-${activeSlot}`)}
				{visibleLayers.map((layer, layerIndex) => {
					const sourceIndex = layers.indexOf(layer);
					return this.renderRecordComponents(layer, getFrame(layer), (sourceIndex + 1) * 10, `layer-${layer.id || layerIndex}`);
				})}
				{!hasBaseArtwork && visibleLayers.length === 0 && (
					<div
						className="floorplan-studio__canvas-empty"
						style={{ width: `${Math.min(canvas.width - 80, 600)}px` }}
					>
						No renderable base or visible option artwork is linked to this floor.
					</div>
				)}
			</div>
		);
	}

	renderLayerList() {
		const { floor, activeSlot } = this.getCurrentContext();
		const layers = asArray(floor && floor.layers);
		if (!layers.length) {
			return <div className="floorplan-studio__empty-list">This floor has no option layers.</div>;
		}

		return layers.map((layer, layerIndex) => {
			const inScope = isLayerInScope(layer, activeSlot);
			const artworkAssetId = getPreferredArtworkAssetId(layer);
			return (
				<label
					key={layer.id !== undefined ? layer.id : layerIndex}
					className={`floorplan-studio__layer${inScope ? "" : " floorplan-studio__layer--inactive"}`}
				>
					<input
						type="checkbox"
						checked={layer.visible !== false}
						onChange={(event) => this.handleLayerVisibilityChange(layerIndex, event)}
					/>
					<div className="floorplan-studio__layer-copy">
						<div className="floorplan-studio__layer-name">{layer.name || `Layer ${layerIndex + 1}`}</div>
						<div className="floorplan-studio__layer-meta">
							{layer.group && <span>{layer.group}</span>}
							{layer.scope && layer.scope !== "both" && <Label bsStyle={inScope ? "info" : "default"}>{layer.scope.toUpperCase()}</Label>}
							{layer.noneOption === true && <Label>None</Label>}
							{!artworkAssetId && layer.noneOption !== true && <Label bsStyle="warning">No artwork</Label>}
						</div>
					</div>
				</label>
			);
		});
	}

	getDiagnostics() {
		const { floor, activeSlot, base, assets } = this.getCurrentContext();
		const layers = asArray(floor && floor.layers);
		const records = _.compact([base]).concat(layers);
		const referencedAssetIds = _.uniq(_.flatten(records.map(getReferencedAssetIds)));
		const missingAssetIds = referencedAssetIds.filter((assetId) => !getAssetDescriptor(assets, assetId));
		const unresolvedAssetIds = referencedAssetIds.filter((assetId) => {
			const asset = getRenderableAsset(this.state.draft, assets, assetId);
			return asset && !getAssetUrl(asset);
		});
		const studioRenditionCount = Object.keys(_.get(this.state.draft, "renditions.profiles.studio.assets", {})).length;
		return {
			layerCount: layers.length,
			visibleLayerCount: layers.filter((layer) => layer && layer.visible !== false && isLayerInScope(layer, activeSlot)).length,
			assetCount: Object.keys(assets).length,
			studioRenditionCount,
			referencedAssetCount: referencedAssetIds.length,
			missingAssetIds,
			unresolvedAssetIds,
			imageErrorIds: Object.keys(this.state.imageErrors),
			hasBase: !!base,
			hasBaseArtwork: !!getPreferredArtworkAssetId(base)
		};
	}

	renderDiagnostics() {
		const diagnostics = this.getDiagnostics();
		const issues = [];
		if (!diagnostics.hasBase) {
			issues.push("The selected base slot has no base record.");
		} else if (!diagnostics.hasBaseArtwork) {
			issues.push("The selected base has no imageAssetId.");
		}
		diagnostics.missingAssetIds.forEach((assetId) => issues.push(`Missing asset descriptor: ${assetId}`));
		diagnostics.unresolvedAssetIds.forEach((assetId) => issues.push(`Drive resource is not available: ${assetId}`));
		diagnostics.imageErrorIds.forEach((assetId) => issues.push(`Image failed to load: ${assetId}`));

		return (
			<div className="floorplan-studio__diagnostics">
				<div className="floorplan-studio__diagnostic-section">
					<div className="floorplan-studio__diagnostic-title">Current floor</div>
					<div className="floorplan-studio__metric"><span>Option layers</span><strong>{diagnostics.layerCount}</strong></div>
					<div className="floorplan-studio__metric"><span>Visible in view</span><strong>{diagnostics.visibleLayerCount}</strong></div>
					<div className="floorplan-studio__metric"><span>Referenced assets</span><strong>{diagnostics.referencedAssetCount}</strong></div>
				</div>
				<div className="floorplan-studio__diagnostic-section">
					<div className="floorplan-studio__diagnostic-title">Project</div>
					<div className="floorplan-studio__metric"><span>Drive assets</span><strong>{diagnostics.assetCount}</strong></div>
					<div className="floorplan-studio__metric"><span>Studio previews</span><strong>{diagnostics.studioRenditionCount}</strong></div>
					<div className="floorplan-studio__metric"><span>Status</span><Label bsStyle={issues.length ? "warning" : "success"}>{issues.length ? `${issues.length} issue${issues.length === 1 ? "" : "s"}` : "Ready"}</Label></div>
				</div>
				{issues.length > 0 && (
					<div className="floorplan-studio__diagnostic-section">
						<div className="floorplan-studio__diagnostic-title">Issues</div>
						{issues.map((issue, index) => <div key={`${issue}-${index}`} className="floorplan-studio__issue">{issue}</div>)}
					</div>
				)}
			</div>
		);
	}

	renderVisualEditor() {
		const { floors, floor, activeSlot } = this.getCurrentContext();
		const labels = getSlotLabels(floor);
		const showSlotSelector = !!(floor && (floor.baseB || floor.variantMode === "entry" || floor.variantMode === "local"));
		const layerCount = asArray(floor && floor.layers).length;

		return (
			<div className="floorplan-studio__visual">
				<div className="floorplan-studio__header">
					<div>
						<div className="floorplan-studio__title">{getDraftTitle(this.state.draft)}</div>
						<div className="floorplan-studio__subtitle">Drive-backed authoring draft · {Object.keys(getAssets(this.state.draft)).length} assets</div>
					</div>
					<div className="floorplan-studio__selectors">
						<div className="floorplan-studio__selector">
							<label htmlFor="floorplan-studio-floor">Floor</label>
							<FormControl
								id="floorplan-studio-floor"
								componentClass="select"
								value={this.state.floorIndex}
								disabled={!floors.length}
								onChange={this.handleFloorChange}
							>
								{floors.length === 0 && <option value={0}>No floors</option>}
								{floors.map((candidate, index) => <option key={candidate.id || candidate.name || index} value={index}>{candidate.name || candidate.label || `Floor ${index + 1}`}</option>)}
							</FormControl>
						</div>
						{showSlotSelector && (
							<div className="floorplan-studio__selector">
								<label htmlFor="floorplan-studio-base">{floor.variantMode === "entry" ? "Entry configuration" : "Base plan"}</label>
								<FormControl id="floorplan-studio-base" componentClass="select" value={activeSlot} onChange={this.handleSlotChange}>
									<option value="a">{labels[0]}</option>
									<option value="b">{labels[1]}</option>
								</FormControl>
							</div>
						)}
					</div>
				</div>

				<div className="floorplan-studio__workspace">
					<div className="floorplan-studio__panel">
						<div className="floorplan-studio__panel-header">
							<div>
								<div className="floorplan-studio__panel-kicker">Composition</div>
								<div className="floorplan-studio__panel-title">Option layers</div>
							</div>
							<Label>{layerCount}</Label>
						</div>
						<div className="floorplan-studio__layers">{this.renderLayerList()}</div>
					</div>

					<div className="floorplan-studio__panel floorplan-studio__canvas-panel">
						<div className="floorplan-studio__canvas-toolbar">
							<ButtonGroup>
								<Button bsSize="small" onClick={this.handleZoomOut} title="Zoom out" aria-label="Zoom out"><FontAwesomeIcon icon="search-minus" /></Button>
								<Button bsSize="small" onClick={this.handleZoomIn} title="Zoom in" aria-label="Zoom in"><FontAwesomeIcon icon="search-plus" /></Button>
								<Button bsSize="small" onClick={this.fitToView} title="Fit floorplan in view"><FontAwesomeIcon icon="expand" /> Fit</Button>
								<Button bsSize="small" onClick={this.handleZoomActual} title="Show at actual pixel size">100%</Button>
							</ButtonGroup>
							<div className="floorplan-studio__zoom-readout">{Math.round(this.state.zoom * 100)}%</div>
						</div>
						<div
							ref={this.viewport}
							className={`floorplan-studio__viewport${this.state.panning ? " floorplan-studio__viewport--panning" : ""}`}
							onMouseDown={this.handlePanMouseDown}
							onWheel={this.handleWheel}
							onAuxClick={(event) => event.preventDefault()}
						>
							{this.renderCanvas()}
						</div>
					</div>

					<div className="floorplan-studio__panel floorplan-studio__panel--diagnostics">
						<div className="floorplan-studio__panel-header">
							<div>
								<div className="floorplan-studio__panel-kicker">Resolver</div>
								<div className="floorplan-studio__panel-title">Diagnostics</div>
							</div>
						</div>
						{this.renderDiagnostics()}
					</div>
				</div>
			</div>
		);
	}

	renderJsonEditor() {
		return (
			<div className="floorplan-studio__json">
				{this.state.jsonError && <Alert bsStyle="danger"><strong>Invalid JSON:</strong> {this.state.jsonError}</Alert>}
				{!this.state.jsonError && this.state.schemaErrors.length > 0 && (
					<Alert bsStyle="danger">
						<strong>This is not a valid Floorplan Studio draft.</strong>
						<ul>{this.state.schemaErrors.map((error) => <li key={error}>{error}</li>)}</ul>
					</Alert>
				)}
				<CodeMirror
					value={this.state.jsonText}
					options={FLOORPLAN_JSON_EDITOR_OPTIONS}
					onBeforeChange={(editor, data, value) => this.handleJsonChange(value)}
					className="floorplan-studio__json-editor"
				/>
			</div>
		);
	}

	render() {
		return (
			<div className="floorplan-studio">
				<Tabs className="floorplan-studio__tabs" activeKey={this.state.activeTab} onSelect={(activeTab) => this.setState({ activeTab }, activeTab === "visual" ? this.requestFit : null)} id="floorplan-studio-editor-tabs">
					<Tab eventKey="visual" title={<span><FontAwesomeIcon icon="layer-group" /> Studio</span>} disabled={!this.state.draft || this.state.schemaErrors.length > 0}>
						{this.state.draft && this.state.schemaErrors.length === 0 ? this.renderVisualEditor() : null}
					</Tab>
					<Tab eventKey="json" title={<span><FontAwesomeIcon icon={["far", "file-code"]} /> JSON</span>}>
						{this.state.activeTab === "json" ? this.renderJsonEditor() : null}
					</Tab>
				</Tabs>
			</div>
		);
	}
}
